/**
 * Exceedios custom cart drawer — Horizon port.
 *
 * A dependency-free rewrite of the original Dawn-era `adev-cart-drawer.min.js`
 * (which needed jQuery + Slick). Behaviour is 1:1 with the original; only the
 * theme integration points differ, because Horizon has none of Dawn's globals
 * (`theme.routes`, `theme.strings`, `Shopify.formatMoney`, lazysizes).
 *
 * Loaded as a classic script so it never depends on Horizon's import map.
 */
(function () {
  'use strict';

  if (window.adevCartDrawer) return;

  var SECTION = 'adev-cart-drawer';
  var DRAWER_ID = 'cart-drawer';
  var QUANTITY_DEBOUNCE = 400;
  var CART_ROUTES = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /** Own requests carry this header so the interceptor ignores them. */
  function adevHeaders(extra) {
    var headers = { 'X-Adev': '1' };
    if (extra) {
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) headers[key] = extra[key];
      }
    }
    return headers;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: adevHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify(body),
    }).then(function (response) {
      if (!response.ok) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            var error = new Error(data.description || data.message || response.statusText);
            error.data = data;
            throw error;
          });
      }
      return response.json();
    });
  }

  /* ------------------------------------------------------------------ *
   * Drawer open / close  (was `adev.min.js` → drawer())
   * ------------------------------------------------------------------ */

  var Drawer = {
    lastFocus: null,

    el: function () {
      return $('[data-adev-drawer="' + DRAWER_ID + '"]');
    },

    overlay: function () {
      return $('[data-adev-drawer-overlay]');
    },

    isOpen: function () {
      var el = this.el();
      return !!el && el.classList.contains('adev-opened');
    },

    open: function () {
      var el = this.el();
      if (!el) return;

      this.lastFocus = document.activeElement;

      el.classList.add('adev-opened');
      el.setAttribute('tabindex', '0');
      el.removeAttribute('aria-hidden');

      var overlay = this.overlay();
      if (overlay) overlay.classList.add('adev-opened');

      document.documentElement.classList.add('adev-noscroll');
      Extras.setFooterHeight();

      // Move focus into the drawer for keyboard + screen-reader users.
      var closeBtn = $('[data-adev-close-drawer]', el);
      (closeBtn || el).focus({ preventScroll: true });

      document.dispatchEvent(new CustomEvent('adev:drawer:opened', { detail: { id: DRAWER_ID } }));
    },

    close: function () {
      var el = this.el();
      if (el) {
        el.classList.remove('adev-opened');
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      }

      var overlay = this.overlay();
      if (overlay) overlay.classList.remove('adev-opened');

      document.documentElement.classList.remove('adev-noscroll');

      // Any open upsell popup closes with the drawer.
      Upsell.closePopup();

      if (this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus({ preventScroll: true });
      }
      this.lastFocus = null;

      document.dispatchEvent(new CustomEvent('adev:drawer:closed', { detail: { id: DRAWER_ID } }));
    },

    /** Keep Tab inside the drawer while it is open. */
    trapFocus: function (event) {
      if (event.key !== 'Tab' || !this.isOpen()) return;

      var el = this.el();
      if (!el) return;

      var focusable = $$(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        el
      ).filter(function (node) {
        return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
      });

      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  };

  /* ------------------------------------------------------------------ *
   * Loading state + section refresh
   * ------------------------------------------------------------------ */

  var Cart = {
    scrollPosition: 0,
    refreshing: false,

    setLoading: function (state) {
      var contents = $('[data-adev-cart-drawer-contents]');
      if (!contents) return;
      contents.classList.toggle('loading', !!state);
    },

    /** Remember where the item list was scrolled so a refresh doesn't jump. */
    scrollElements: function () {
      return $$(
        '.adev-drawer__full-scroll .adev-cart-drawer__inner, .adev-drawer__limited-scroll .adev-cart-drawer__items'
      );
    },

    watchScroll: function () {
      var self = this;
      this.scrollElements().forEach(function (el) {
        if (el.dataset.adevScrollBound === 'true') return;
        el.dataset.adevScrollBound = 'true';
        el.addEventListener(
          'scroll',
          function () {
            self.scrollPosition = el.scrollTop;
          },
          { passive: true }
        );
      });
    },

    restoreScroll: function () {
      var self = this;
      this.scrollElements().forEach(function (el) {
        el.scrollTop = self.scrollPosition;
      });
    },

    itemCount: function () {
      var form = $('[data-adev-cart-drawer-form]');
      if (!form) return 0;
      return parseInt(form.getAttribute('data-adev-count') || '0', 10) || 0;
    },

    /**
     * Re-render the drawer from the Section Rendering API.
     *
     * NOTE: this must hit the *root* path. `/cart?sections=…` returns the cart
     * JSON object instead of section HTML.
     */
    refresh: function (sectionsHTML) {
      var self = this;

      var apply = function (html) {
        if (!html) return;

        var parsed = document.createElement('div');
        parsed.innerHTML = html;

        var fresh = $('[data-adev-cart-drawer-contents]', parsed);
        var current = $('[data-adev-cart-drawer-contents]');

        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
        }

        self.restoreScroll();
        self.watchScroll();
        Extras.setFooterHeight();
        Upsell.init();
        ShippingProtection.init();
        self.setLoading(false);

        // Keep Horizon's header bubble in sync.
        Bridge.publishCount(self.itemCount());

        document.dispatchEvent(new CustomEvent('adev:cart:drawer:reinit'));
      };

      if (sectionsHTML && sectionsHTML[SECTION]) {
        apply(sectionsHTML[SECTION]);
        return Promise.resolve();
      }

      if (this.refreshing) return Promise.resolve();
      this.refreshing = true;

      return fetch(rootUrl() + '?sections=' + SECTION + '&t=' + Date.now(), {
        headers: adevHeaders({ Accept: 'application/json' }),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          apply(data[SECTION]);
        })
        .catch(function (error) {
          console.error('[adev-cart-drawer] refresh failed', error);
          self.setLoading(false);
        })
        .finally(function () {
          self.refreshing = false;
        });
    },

    /** Add a variant, refresh, and open the drawer. */
    add: function (variantId, quantity) {
      var self = this;
      this.setLoading(true);

      return postJSON('/cart/add.js', {
        items: [{ id: variantId, quantity: quantity || 1 }],
        sections: SECTION,
      })
        .then(function (data) {
          return self.refresh(data.sections);
        })
        .catch(function (error) {
          self.setLoading(false);
          throw error;
        });
    },

    change: function (payload) {
      var self = this;
      this.setLoading(true);

      payload.sections = SECTION;

      return postJSON('/cart/change.js', payload)
        .then(function (data) {
          return self.refresh(data.sections);
        })
        .catch(function (error) {
          self.setLoading(false);
          throw error;
        });
    },

    /** Push the whole line-item form through /cart/update.js. */
    updateFromForm: function () {
      var form = $('[data-adev-cart-drawer-form]');
      if (!form) return Promise.resolve();

      var updates = {};
      $$('input[name^="updates["]', form).forEach(function (input) {
        var match = input.name.match(/^updates\[(.+)\]$/);
        if (!match) return;
        var value = parseInt(input.value, 10);
        updates[match[1]] = isNaN(value) ? 0 : value;
      });

      var self = this;
      this.setLoading(true);

      return postJSON('/cart/update.js', { updates: updates, sections: SECTION })
        .then(function (data) {
          return self.refresh(data.sections);
        })
        .catch(function (error) {
          self.setLoading(false);
          console.error('[adev-cart-drawer] update failed', error);
        });
    },
  };

  /* ------------------------------------------------------------------ *
   * Free-shipping bar height var + shared bits
   * ------------------------------------------------------------------ */

  var Extras = {
    setFooterHeight: function () {
      var footer = $('[data-adev-cart-drawer-footer]');
      var height = footer ? footer.offsetHeight : 0;
      document.documentElement.style.setProperty('--adev-cart-drawer-footer-height', height + 'px');
    },
  };

  /* ------------------------------------------------------------------ *
   * Upsell carousel + variant popup
   * ------------------------------------------------------------------ */

  var Upsell = {
    index: 0,
    products: {},

    init: function () {
      this.initCarousel();
      this.initPopup();
    },

    /**
     * Dependency-free replacement for Slick. Builds the same DOM shape
     * (.slick-list > .slick-track) and reuses Slick's class names, so every
     * ported `.slick-*` rule in the stylesheet still applies.
     */
    initCarousel: function () {
      var self = this;

      $$('[data-adev-cart-drawer-upsell="true"]').forEach(function (container) {
        if (container.classList.contains('slick-initialized')) return;

        var slides = $$('.adev-upsell-item-col', container);
        if (!slides.length) return;

        var list = document.createElement('div');
        list.className = 'slick-list';

        var track = document.createElement('div');
        track.className = 'slick-track';

        slides.forEach(function (slide) {
          track.appendChild(slide);
        });

        list.appendChild(track);
        container.appendChild(list);

        var prev = document.createElement('button');
        prev.type = 'button';
        prev.className = 'slick-arrow slick-prev-arrow';
        prev.setAttribute('aria-label', 'Previous product');
        prev.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><g stroke-width="1.5" fill="none"><path d="M15 6l-6 6l6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></g></svg>';

        var next = document.createElement('button');
        next.type = 'button';
        next.className = 'slick-arrow slick-next-arrow';
        next.setAttribute('aria-label', 'Next product');
        next.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><g stroke-width="1.5" fill="none"><path d="M9 6l6 6l-6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></g></svg>';

        container.insertBefore(prev, list);
        container.appendChild(next);
        container.classList.add('slick-initialized');

        var total = slides.length;

        // `infinite:false` in the original — clamp instead of wrapping.
        if (self.index > total - 1) self.index = total - 1;
        if (self.index < 0) self.index = 0;

        var render = function () {
          track.style.transform = 'translateX(' + -100 * self.index + '%)';
          prev.classList.toggle('slick-disabled', self.index <= 0);
          next.classList.toggle('slick-disabled', self.index >= total - 1);
          prev.disabled = self.index <= 0;
          next.disabled = self.index >= total - 1;

          slides.forEach(function (slide, i) {
            slide.classList.toggle('slick-active', i === self.index);
            slide.setAttribute('aria-hidden', i === self.index ? 'false' : 'true');
          });
        };

        prev.addEventListener('click', function (event) {
          event.preventDefault();
          if (self.index <= 0) return;
          self.index -= 1;
          render();
        });

        next.addEventListener('click', function (event) {
          event.preventDefault();
          if (self.index >= total - 1) return;
          self.index += 1;
          render();
        });

        render();
      });
    },

    /** Read the per-product variant JSON emitted by the Liquid. */
    initPopup: function () {
      var self = this;

      $$('[data-adev-upsell-popup]').forEach(function (popup) {
        if (popup.classList.contains('adev-initialized')) return;
        popup.classList.add('adev-initialized');

        $$('[data-uspp-upsell-product]', popup).forEach(function (productEl) {
          var script = $('script[type="application/json"]', productEl);
          if (!script) return;

          var data;
          try {
            data = JSON.parse(script.textContent);
          } catch (error) {
            console.error('[adev-cart-drawer] bad upsell JSON', error);
            return;
          }

          self.products[data.id] = data;

          $$('[data-uspp-variant-option-input]', productEl).forEach(function (select) {
            select.addEventListener('change', function () {
              self.updateVariant(productEl, data.id);
            });
          });
        });
      });
    },

    openPopup: function (productId) {
      var popup = $('[data-adev-upsell-popup]');
      var product = $('[data-uspp-upsell-product="' + productId + '"]');
      if (!popup || !product) return;

      $$('[data-uspp-upsell-product].active').forEach(function (el) {
        el.classList.remove('active');
      });

      popup.classList.add('active');
      product.classList.add('active');
    },

    closePopup: function () {
      $$('[data-adev-upsell-popup].active').forEach(function (el) {
        el.classList.remove('active');
      });
      $$('[data-uspp-upsell-product].active').forEach(function (el) {
        el.classList.remove('active');
      });
    },

    /**
     * Match the selected option values against the variant list and repaint
     * title, price, availability and image.
     *
     * Prices are pre-formatted by Liquid (Horizon has no `Shopify.formatMoney`),
     * which also keeps Shopify Markets pricing correct.
     */
    updateVariant: function (productEl, productId) {
      var data = this.products[productId];
      if (!data) return;

      var chosen = $$('[data-uspp-variant-option-input]', productEl).map(function (select) {
        return select.value;
      });

      var variant = data.variants.filter(function (candidate) {
        return candidate.options.every(function (option, index) {
          return chosen[index] === option;
        });
      })[0];

      var addButton = $('[data-uspp-add-to-cart]', productEl);
      var prices = $('[data-uspp-product-prices]', productEl);
      var regular = $('[data-uspp-regular-price]', productEl);
      var compare = $('[data-uspp-compare-at-price]', productEl);
      var variantTitle = $('[data-uspp-variant-title]', productEl);
      var image = $('.adev-uspp__image img', productEl);

      if (!variant) {
        if (addButton) {
          addButton.classList.add('disabled');
          var missing = $('span', addButton);
          if (missing) missing.textContent = 'Unavailable';
        }
        return;
      }

      data.current_variant_id = variant.id;

      if (variantTitle) variantTitle.textContent = variant.title;

      if (addButton) {
        addButton.setAttribute('href', '/cart/add/' + variant.id);
        addButton.setAttribute('data-adev-product-id', variant.id);
        addButton.classList.toggle('disabled', !variant.available);
        var label = $('span', addButton);
        if (label) label.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }

      if (regular) regular.innerHTML = variant.price;

      if (prices) prices.setAttribute('data-on-sale', variant.on_sale ? 'true' : 'false');

      if (compare) {
        if (variant.on_sale && variant.compare_at_price) {
          compare.style.display = '';
          compare.innerHTML = '<s>' + variant.compare_at_price + '</s>';
        } else {
          compare.style.display = 'none';
          compare.innerHTML = '';
        }
      }

      if (image && variant.image) image.setAttribute('src', variant.image);
    },
  };

  /* ------------------------------------------------------------------ *
   * Shipping protection
   * ------------------------------------------------------------------ */

  var ShippingProtection = {
    init: function () {
      $$('[data-adev-shipping-protection]').forEach(function (block) {
        if (block.classList.contains('adev-initialized')) return;
        block.classList.add('adev-initialized');
      });
    },

    toggle: function (button) {
      var variantId = button.getAttribute('data-id');
      if (!variantId) return;

      var wasActive = button.classList.contains('active');
      button.classList.toggle('active');
      button.setAttribute('aria-checked', wasActive ? 'false' : 'true');

      var request = wasActive
        ? Cart.change({ id: variantId, quantity: 0 })
        : Cart.add(variantId, 1);

      request.catch(function (error) {
        // Roll the toggle back if the cart rejected the change.
        button.classList.toggle('active');
        button.setAttribute('aria-checked', wasActive ? 'true' : 'false');
        console.error('[adev-cart-drawer] shipping protection failed', error);
      });
    },

    /**
     * Optional auto-add, gated by a 48h localStorage flag so a shopper who
     * removes it doesn't get it silently re-added on every cart change.
     */
    maybeAutoAdd: function () {
      var config = window.adev_shipping_protection;
      if (!config || !config.enabled || !config.autoAdd || !config.productID) return false;
      if (config.alreadyAdded) return false;
      if (window.Shopify && window.Shopify.designMode) return false;
      if (Storage.get('adev_shipping_protection') === 'true') return false;
      if (Cart.itemCount() === 0) return false;

      Storage.set('adev_shipping_protection', 'true', 172800000);

      Cart.add(config.productID, 1)
        .then(function () {
          Drawer.open();
        })
        .catch(function (error) {
          console.error('[adev-cart-drawer] auto-add failed', error);
        });

      return true;
    },
  };

  /* ------------------------------------------------------------------ *
   * localStorage with expiry (was `adev.min.js`)
   * ------------------------------------------------------------------ */

  var Storage = {
    set: function (key, value, ttl) {
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ value: value, expiration: ttl ? Date.now() + ttl : null })
        );
      } catch (error) {
        /* private mode — non-fatal */
      }
    },

    get: function (key) {
      try {
        var raw = JSON.parse(localStorage.getItem(key));
        if (!raw) return null;
        if (raw.expiration && Date.now() > raw.expiration) {
          localStorage.removeItem(key);
          return null;
        }
        return raw.value;
      } catch (error) {
        return null;
      }
    },
  };

  /* ------------------------------------------------------------------ *
   * Horizon bridge
   * ------------------------------------------------------------------ */

  var Bridge = {
    /**
     * Horizon's `cart-icon.js` syncs its bubble off `cart:update`:
     *   detail.data.itemCount, and
     *   detail.data.source === 'product-form-component' means ADD to the
     *   running count; anything else means SET it.
     * We always send an absolute count under our own source, and ignore that
     * source on the way back in so the two never loop.
     */
    publishCount: function (count) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: 'adev-cart-drawer',
            data: { source: 'adev-cart-drawer', itemCount: count, didError: false },
          },
        })
      );
    },

    listen: function () {
      document.addEventListener('cart:update', function (event) {
        var detail = (event.detail && event.detail.data) || {};
        if (detail.source === 'adev-cart-drawer') return;
        if (detail.didError) return;

        var isAdd = detail.source === 'product-form-component';

        Cart.refresh().then(function () {
          if (isAdd) Drawer.open();
        });
      });
    },

    /**
     * Not every add goes through Horizon's product form — apps and custom
     * buttons post straight to /cart/*.js and emit nothing. Intercept those
     * so the drawer still refreshes and opens. Our own requests are tagged
     * `X-Adev: 1` and skipped.
     */
    intercept: function () {
      var isOwn = function (init) {
        if (!init) return false;
        var headers = init.headers;
        if (!headers) return false;
        if (typeof headers.get === 'function') return !!headers.get('X-Adev');
        return !!(headers['X-Adev'] || headers['x-adev']);
      };

      var handle = function (url, isAdd) {
        if (!CART_ROUTES.test(url)) return;
        setTimeout(function () {
          Cart.refresh().then(function () {
            if (isAdd) Drawer.open();
          });
        }, 0);
      };

      var nativeFetch = window.fetch;
      if (nativeFetch) {
        window.fetch = function (input, init) {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          var own = isOwn(init) || (input && input.headers && isOwn({ headers: input.headers }));

          return nativeFetch.apply(this, arguments).then(function (response) {
            if (!own && response && response.ok) {
              handle(String(url), /\/cart\/add/.test(String(url)));
            }
            return response;
          });
        };
      }

      var open = XMLHttpRequest.prototype.open;
      var send = XMLHttpRequest.prototype.send;
      var setHeader = XMLHttpRequest.prototype.setRequestHeader;

      XMLHttpRequest.prototype.open = function (method, url) {
        this.__adevUrl = String(url || '');
        return open.apply(this, arguments);
      };

      XMLHttpRequest.prototype.setRequestHeader = function (name) {
        if (String(name).toLowerCase() === 'x-adev') this.__adevOwn = true;
        return setHeader.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        var self = this;
        this.addEventListener('load', function () {
          if (self.__adevOwn) return;
          if (self.status < 200 || self.status >= 300) return;
          handle(self.__adevUrl || '', /\/cart\/add/.test(self.__adevUrl || ''));
        });
        return send.apply(this, arguments);
      };
    },
  };

  /* ------------------------------------------------------------------ *
   * Delegated events
   * ------------------------------------------------------------------ */

  function bindEvents() {
    // --- open / close ------------------------------------------------
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-adev-open-drawer]');
      if (opener) {
        event.preventDefault();
        Drawer.open();
        return;
      }

      var closer = event.target.closest('[data-adev-close-drawer]');
      if (closer) {
        event.preventDefault();
        Drawer.close();
        return;
      }

      if (event.target.closest('[data-adev-drawer-overlay]')) {
        event.preventDefault();
        Drawer.close();
        return;
      }

      // --- remove line item -----------------------------------------
      var remove = event.target.closest('[data-adev-remove-cart-drawer-item]');
      if (remove) {
        event.preventDefault();
        Cart.change({ id: remove.getAttribute('data-key'), quantity: 0 }).catch(function (error) {
          console.error('[adev-cart-drawer] remove failed', error);
        });
        return;
      }

      // --- quantity stepper -----------------------------------------
      var up = event.target.closest('[data-adev-quantity-up]');
      var down = event.target.closest('[data-adev-quantity-down]');
      if (up || down) {
        event.preventDefault();
        stepQuantity(up || down, up ? 1 : -1);
        return;
      }

      // --- upsell ---------------------------------------------------
      var upsellAdd = event.target.closest('[data-adev-upsell-add]');
      if (upsellAdd) {
        event.preventDefault();
        Upsell.openPopup(upsellAdd.getAttribute('data-adev-upsell-add'));
        return;
      }

      var popup = event.target.closest('[data-adev-upsell-popup]');
      if (popup && event.target === popup) {
        event.preventDefault();
        Upsell.closePopup();
        return;
      }

      var popupAdd = event.target.closest('[data-uspp-add-to-cart]');
      if (popupAdd) {
        event.preventDefault();
        if (popupAdd.classList.contains('disabled') || popupAdd.classList.contains('loading')) return;

        var label = $('span', popupAdd);
        var original = label ? label.textContent : '';

        popupAdd.classList.add('loading');
        if (label) label.textContent = 'Adding...';

        Cart.add(popupAdd.getAttribute('data-adev-product-id'), 1)
          .then(function () {
            Upsell.closePopup();
          })
          .catch(function (error) {
            console.error('[adev-cart-drawer] upsell add failed', error);
          })
          .finally(function () {
            popupAdd.classList.remove('loading');
            if (label) label.textContent = original || 'Add to Cart';
          });
        return;
      }

      // --- shipping protection --------------------------------------
      var protection = event.target.closest('[data-adev-shipping-protection-atc]');
      if (protection) {
        event.preventDefault();
        ShippingProtection.toggle(protection);
      }
    });

    // Keyboard support for the protection toggle (it is a div, not an input).
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        var openPopup = $('[data-adev-upsell-popup].active');
        if (openPopup) {
          Upsell.closePopup();
          return;
        }
        if (Drawer.isOpen()) Drawer.close();
        return;
      }

      if (event.key === 'Tab') {
        Drawer.trapFocus(event);
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        var protection = event.target.closest && event.target.closest('[data-adev-shipping-protection-atc]');
        if (protection) {
          event.preventDefault();
          ShippingProtection.toggle(protection);
        }
      }
    });

    // --- debounced quantity → cart update ---------------------------
    var pushQuantity = debounce(function () {
      Cart.updateFromForm();
    }, QUANTITY_DEBOUNCE);

    document.addEventListener('change', function (event) {
      if (!event.target.closest('[data-adev-cart-drawer-form]')) return;
      if (!event.target.matches('[data-adev-quantity-value]')) return;
      Cart.setLoading(true);
      pushQuantity();
    });

    // --- checkout ----------------------------------------------------
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-adev-cart-drawer-form]');
      if (!form) return;
      // Let the native POST to /cart proceed — `name="checkout"` on the
      // submit button is what sends the shopper to checkout.
    });

    window.addEventListener('resize', debounce(Extras.setFooterHeight, 150));
  }

  function stepQuantity(button, direction) {
    var wrapper = button.closest('[data-adev-quantity-selector]');
    if (!wrapper) return;

    var input = $('[data-adev-quantity-value]', wrapper);
    if (!input) return;

    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    if (isNaN(min)) min = 0;
    if (isNaN(max)) max = 1000;

    var value = parseInt(input.value, 10);
    if (isNaN(value)) value = 0;

    var next = value + direction;
    if (next < min || next > max) return;

    input.value = next;
    input.setAttribute('value', next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------------ *
   * Theme editor
   * ------------------------------------------------------------------ */

  function bindThemeEditor() {
    document.addEventListener('shopify:section:load', function (event) {
      if (!event.target || !event.target.querySelector) return;
      if (!event.target.querySelector('[data-adev-drawer]') && !$('[data-adev-drawer]')) return;
      Upsell.init();
      ShippingProtection.init();
      Cart.watchScroll();
      Extras.setFooterHeight();
    });

    document.addEventListener('shopify:section:select', function (event) {
      if (event.target && event.target.querySelector && event.target.querySelector('[data-adev-drawer]')) {
        Drawer.open();
      }
    });

    document.addEventListener('shopify:section:deselect', function (event) {
      if (event.target && event.target.querySelector && event.target.querySelector('[data-adev-drawer]')) {
        Drawer.close();
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init() {
    if (!$('[data-adev-drawer="' + DRAWER_ID + '"]')) return;

    Cart.watchScroll();
    Extras.setFooterHeight();
    Upsell.init();
    ShippingProtection.init();
    ShippingProtection.maybeAutoAdd();
  }

  window.adevCartDrawer = {
    open: function () {
      Drawer.open();
    },
    close: function () {
      Drawer.close();
    },
    refresh: function () {
      return Cart.refresh();
    },
    add: function (variantId, quantity) {
      return Cart.add(variantId, quantity);
    },
  };

  bindEvents();
  bindThemeEditor();
  Bridge.listen();
  Bridge.intercept();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
